import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Clock,
  CheckCircle2,
  XCircle,
  Save,
  Image,
  ExternalLink,
  Inbox,
  Bug,
  Sparkles,
  Database,
  Monitor,
  Zap,
  MoreHorizontal,
  Loader2,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { getApiBaseUrl } from '../lib/getApiUrl';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IssueStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
type IssuePriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
type IssueCategory = 'Bug' | 'Feature Request' | 'Data Issue' | 'UI Problem' | 'Performance' | 'Other';

interface IssueReporter {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface Issue {
  id: string;
  reportedBy: IssueReporter;
  description: string;
  page: string;
  category: IssueCategory;
  priority: IssuePriority;
  status: IssueStatus;
  resolution?: string | null;
  screenshotUrl?: string | null;
  hasScreenshot?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Constants & helpers
// ---------------------------------------------------------------------------

const API_URL = getApiBaseUrl();

const STATUS_OPTIONS: IssueStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const PRIORITY_OPTIONS: IssuePriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const STATUS_BADGE: Record<IssueStatus, { bg: string; text: string; icon: React.ReactNode }> = {
  OPEN: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <AlertCircle className="w-3 h-3" /> },
  IN_PROGRESS: { bg: 'bg-amber-100', text: 'text-amber-800', icon: <Clock className="w-3 h-3" /> },
  RESOLVED: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle2 className="w-3 h-3" /> },
  CLOSED: { bg: 'bg-gray-100', text: 'text-gray-600', icon: <XCircle className="w-3 h-3" /> },
};

const PRIORITY_BADGE: Record<IssuePriority, { bg: string; text: string }> = {
  LOW: { bg: 'bg-gray-100', text: 'text-gray-700' },
  MEDIUM: { bg: 'bg-blue-100', text: 'text-blue-800' },
  HIGH: { bg: 'bg-orange-100', text: 'text-orange-800' },
  CRITICAL: { bg: 'bg-red-100', text: 'text-red-800' },
};

const CATEGORY_BADGE: Record<IssueCategory, { bg: string; text: string; icon: React.ReactNode }> = {
  Bug: { bg: 'bg-red-100', text: 'text-red-800', icon: <Bug className="w-3 h-3" /> },
  'Feature Request': { bg: 'bg-purple-100', text: 'text-purple-800', icon: <Sparkles className="w-3 h-3" /> },
  'Data Issue': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: <Database className="w-3 h-3" /> },
  'UI Problem': { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Monitor className="w-3 h-3" /> },
  Performance: { bg: 'bg-orange-100', text: 'text-orange-800', icon: <Zap className="w-3 h-3" /> },
  Other: { bg: 'bg-gray-100', text: 'text-gray-700', icon: <MoreHorizontal className="w-3 h-3" /> },
};

function formatStatusLabel(s: IssueStatus): string {
  return s.replace('_', ' ');
}

function roleBadge(role: string) {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    CEO: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'CEO' },
    GROUP_MANAGER: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'GM' },
    MANAGER: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'MGR' },
    EXECUTIVE: { bg: 'bg-green-100', text: 'text-green-800', label: 'EXEC' },
  };
  const m = map[role] || { bg: 'bg-gray-100', text: 'text-gray-700', label: role };
  return (
    <span className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${m.bg} ${m.text}`}>
      {m.label}
    </span>
  );
}

function truncate(str: string, max: number): string {
  if (str.length <= max) return str;
  return str.slice(0, max) + '...';
}

function shortenPage(page: string): string {
  try {
    const url = new URL(page, 'http://placeholder');
    return url.pathname;
  } catch {
    return page;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: undefined });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IssueManagementPage() {
  const { user, token } = useAuth();
  const { toast } = useToast();

  // Access control
  const canAccess =
    user?.role === 'CEO' || user?.role === 'GROUP_MANAGER' || user?.role === 'MANAGER';

  // State
  const [issues, setIssues] = useState<Issue[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<IssueStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<IssuePriority | ''>('');
  const [page, setPage] = useState(1);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string | null>>({});
  const [screenshotLoading, setScreenshotLoading] = useState<Record<string, boolean>>({});

  // Editable fields per issue (keyed by issue id)
  const [editResolution, setEditResolution] = useState<Record<string, string>>({});
  const [editStatus, setEditStatus] = useState<Record<string, IssueStatus>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Fetch issues
  // -----------------------------------------------------------------------

  const fetchIssues = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (statusFilter) params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);

      const res = await fetch(`${API_URL}/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error(`Failed to fetch issues (${res.status})`);

      const json = await res.json();
      const data = json.data ?? json;
      setIssues(Array.isArray(data.issues) ? data.issues : Array.isArray(data) ? data : []);
      if (data.pagination) {
        setPagination(data.pagination);
      } else if (data.meta) {
        setPagination(data.meta);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, priorityFilter]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  // -----------------------------------------------------------------------
  // Load screenshot for expanded issue
  // -----------------------------------------------------------------------

  const loadScreenshot = useCallback(
    async (issueId: string) => {
      if (screenshotUrls[issueId] !== undefined || screenshotLoading[issueId]) return;
      setScreenshotLoading((prev) => ({ ...prev, [issueId]: true }));
      try {
        const res = await fetch(`${API_URL}/issues/${issueId}/screenshot`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) {
            setScreenshotUrls((prev) => ({ ...prev, [issueId]: URL.createObjectURL(blob) }));
          } else {
            setScreenshotUrls((prev) => ({ ...prev, [issueId]: null }));
          }
        } else {
          setScreenshotUrls((prev) => ({ ...prev, [issueId]: null }));
        }
      } catch {
        setScreenshotUrls((prev) => ({ ...prev, [issueId]: null }));
      } finally {
        setScreenshotLoading((prev) => ({ ...prev, [issueId]: false }));
      }
    },
    [token, screenshotUrls, screenshotLoading],
  );

  // -----------------------------------------------------------------------
  // Expand / collapse
  // -----------------------------------------------------------------------

  const toggleExpand = (issue: Issue) => {
    if (expandedId === issue.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(issue.id);
    // Seed edit fields
    setEditResolution((prev) => ({ ...prev, [issue.id]: issue.resolution || '' }));
    setEditStatus((prev) => ({ ...prev, [issue.id]: issue.status }));
    // Load screenshot
    if (issue.hasScreenshot !== false) {
      loadScreenshot(issue.id);
    }
  };

  // -----------------------------------------------------------------------
  // Save issue update
  // -----------------------------------------------------------------------

  const handleSave = async (issueId: string) => {
    if (!token) return;
    setSaving((prev) => ({ ...prev, [issueId]: true }));
    try {
      const body: Record<string, any> = {};
      if (editStatus[issueId]) body.status = editStatus[issueId];
      if (editResolution[issueId] !== undefined) body.resolution = editResolution[issueId];

      const res = await fetch(`${API_URL}/issues/${issueId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Update failed');

      // Update local state
      setIssues((prev) =>
        prev.map((iss) =>
          iss.id === issueId
            ? { ...iss, status: editStatus[issueId] ?? iss.status, resolution: editResolution[issueId] ?? iss.resolution }
            : iss,
        ),
      );
      toast({ title: 'Issue updated successfully', description: 'Status and resolution have been saved.' });
    } catch (e: any) {
      toast({ title: 'Update Failed', description: e.message || 'Failed to update issue', variant: 'destructive' });
    } finally {
      setSaving((prev) => ({ ...prev, [issueId]: false }));
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (!canAccess) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-500">You don't have permission to view issue management.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Issue Management</h1>
            <p className="text-gray-600 mt-1">Track and resolve reported issues from your team.</p>
          </div>
          <button
            onClick={() => fetchIssues()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3">
          {/* Status filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as IssueStatus | '');
                setPage(1);
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {formatStatusLabel(s)}
                </option>
              ))}
            </select>
          </div>

          {/* Priority filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Priority</label>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value as IssuePriority | '');
                setPage(1);
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Result count */}
          <span className="ml-auto text-xs text-gray-400">
            {pagination.total} issue{pagination.total !== 1 ? 's' : ''} found
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {loading && issues.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500 mr-2" />
              <span className="text-gray-500">Loading issues...</span>
            </div>
          ) : issues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Inbox className="w-10 h-10 mb-3" />
              <p className="text-sm">No issues found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reported By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Page</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">
                      <span className="sr-only">Expand</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {issues.map((issue, idx) => {
                    const isExpanded = expandedId === issue.id;
                    const rowNum = (pagination.page - 1) * pagination.limit + idx + 1;
                    const sBadge = STATUS_BADGE[issue.status] || STATUS_BADGE.OPEN;
                    const pBadge = PRIORITY_BADGE[issue.priority] || PRIORITY_BADGE.MEDIUM;
                    const cBadge = CATEGORY_BADGE[issue.category] || CATEGORY_BADGE.Other;

                    return (
                      <React.Fragment key={issue.id}>
                        {/* Main row */}
                        <tr
                          onClick={() => toggleExpand(issue)}
                          className={`group cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/50' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-4 py-3 text-gray-400 font-mono text-xs">{rowNum}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="font-medium text-gray-900">
                              {issue.reportedBy?.name || 'Unknown'}
                            </span>
                            {issue.reportedBy?.role && roleBadge(issue.reportedBy.role)}
                          </td>
                          <td className="px-4 py-3 text-gray-700 max-w-xs">
                            {truncate(issue.description, 80)}
                          </td>
                          <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">
                            {shortenPage(issue.page)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cBadge.bg} ${cBadge.text}`}>
                              {cBadge.icon}
                              {issue.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${pBadge.bg} ${pBadge.text}`}>
                              {issue.priority}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sBadge.bg} ${sBadge.text}`}>
                              {sBadge.icon}
                              {formatStatusLabel(issue.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                            {formatDate(issue.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 text-blue-500 group-hover:text-blue-700 transition-colors" title={isExpanded ? 'Click to collapse' : 'Click to expand'}>
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded detail panel */}
                        {isExpanded && (
                          <tr>
                            <td colSpan={9} className="px-0 py-0">
                              <div className="bg-gray-50 border-t border-b border-gray-200 px-6 py-5 space-y-4">
                                {/* Full description */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Full Description</h4>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                                    {issue.description}
                                  </p>
                                </div>

                                {/* Screenshot */}
                                <div>
                                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Screenshot</h4>
                                  {screenshotLoading[issue.id] ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-400">
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                      Loading screenshot...
                                    </div>
                                  ) : screenshotUrls[issue.id] ? (
                                    <div
                                      className="inline-block cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxUrl(screenshotUrls[issue.id]!);
                                      }}
                                    >
                                      <img
                                        src={screenshotUrls[issue.id]!}
                                        alt="Issue screenshot"
                                        className="max-h-48 object-contain"
                                      />
                                      <div className="bg-gray-100 px-3 py-1 text-xs text-gray-500 flex items-center gap-1">
                                        <ExternalLink className="w-3 h-3" />
                                        Click to view full size
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-400 flex items-center gap-1">
                                      <Image className="w-4 h-4" />
                                      No screenshot attached
                                    </p>
                                  )}
                                </div>

                                {/* Resolution + status */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                      Resolution Notes
                                    </label>
                                    <textarea
                                      rows={3}
                                      value={editResolution[issue.id] ?? ''}
                                      onChange={(e) =>
                                        setEditResolution((prev) => ({ ...prev, [issue.id]: e.target.value }))
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Add resolution notes..."
                                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                                      Update Status
                                    </label>
                                    <select
                                      value={editStatus[issue.id] ?? issue.status}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setEditStatus((prev) => ({
                                          ...prev,
                                          [issue.id]: e.target.value as IssueStatus,
                                        }));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                      {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                          {formatStatusLabel(s)}
                                        </option>
                                      ))}
                                    </select>

                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSave(issue.id);
                                      }}
                                      disabled={saving[issue.id]}
                                      className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                    >
                                      {saving[issue.id] ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Save className="w-4 h-4" />
                                      )}
                                      Save Changes
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 px-4 py-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Lightbox overlay */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={lightboxUrl}
              alt="Issue screenshot (full size)"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <button
              onClick={() => setLightboxUrl(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
